import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  dealerApi, BuyerVehicle, DealerBooking, DealerBookingQuote, DealerBookingRequest, FoundCustomer,
  GarageSearchRow, GarageServiceRow, OfferingV2, PickupTimeSlot, Showroom, TransportType,
} from '../../services/dealerApi';
import CustomerLookup from './CustomerLookup';
import ShowroomPicker from './ShowroomPicker';
import Money from './Money';
import { dealerErrorMessage, errorCodeOf, fmt, formatAed, formatDate } from '../../utils/dealer';
import { ApiError } from '../../services/api';
import { DEALER_WIZARD_STORAGE_KEY } from '../../hooks/useAuth';

/**
 * Spec 085 (§2d, Flow H, BR-19) — book on behalf of a buyer:
 * Buyer → Car → Garage/Service → Slot → Pickup/Return → Quote → Confirm.
 *
 * The buyer step finds the registered customer by email + phone (BR-8, D-152) and needs LIVE credit from this
 * dealer; the booking sends the same email + phone and the server re-checks them against the customer.
 *
 * The garage/service step uses the same public catalog the app uses (garage search, the garage's
 * services, services-v2 variations + resolve-quote for the buyer's car). Pickup and return are a dealer
 * showroom (leg by Garagat) or the buyer at the garage (SELF); the dealer cannot see the buyer's own
 * addresses (G14). The booking must be fully covered by the buyer's credit from this dealer: the quote's
 * `toPayNowAed` must be 0, else "Exceeds the buyer's credit by AED X" and no confirm. Wizard state lives in
 * sessionStorage until the booking is confirmed.
 */

export const WIZARD_STORAGE_KEY = DEALER_WIZARD_STORAGE_KEY;

const STEPS = ['buyer', 'car', 'service', 'slot', 'legs', 'quote', 'confirm'] as const;
type StepKey = typeof STEPS[number];

type LegMode = 'SHOWROOM' | 'SELF';

export interface WizardState {
  step: number;
  /** The confirmed customer (BR-8): its email + phone go with the booking, which re-checks them. */
  buyer: { customerId: string; displayName: string | null; email: string; phone: string; remainingAed: string } | null;
  vehicle: BuyerVehicle | null;
  garage: Pick<GarageSearchRow, 'id' | 'name'> | null;
  service: Pick<GarageServiceRow, 'id' | 'name'> | null;
  /** variationId → selected value id (single choice per variation). */
  selectedValues: Record<string, string>;
  variantIds: string[];
  /** True once the service is priceable for the car (flat, or a resolved variation selection). */
  serviceReady: boolean;
  date: string;
  pickupSlotId: string | null;
  returnSlotId: string | null;
  pickupMode: LegMode;
  pickupShowroomId: string | null;
  returnMode: LegMode;
  dropoffShowroomId: string | null;
  transportType: TransportType;
  notes: string;
}

export function emptyWizard(): WizardState {
  return {
    step: 0, buyer: null, vehicle: null, garage: null, service: null, selectedValues: {}, variantIds: [],
    serviceReady: false, date: '', pickupSlotId: null, returnSlotId: null, pickupMode: 'SHOWROOM',
    pickupShowroomId: null, returnMode: 'SHOWROOM', dropoffShowroomId: null, transportType: 'RECOVERY', notes: '',
  };
}

function loadWizard(): WizardState {
  try {
    const raw = sessionStorage.getItem(WIZARD_STORAGE_KEY);
    if (raw) {
      const saved = { ...emptyWizard(), ...JSON.parse(raw) } as WizardState;
      // A wizard saved before BR-8 has no buyer contacts to re-check: start it again.
      if (saved.buyer && (!saved.buyer.email || !saved.buyer.phone)) return emptyWizard();
      return saved;
    }
  } catch {
    // Corrupt or unavailable storage — start fresh.
  }
  return emptyWizard();
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** The quote/booking body for the current wizard state. */
export function toBookingRequest(s: WizardState, expectedTotalPayable?: number): DealerBookingRequest {
  const pickupMethod = s.pickupMode === 'SHOWROOM' ? 'GARAGAT' : 'SELF';
  const returnMethod = s.returnMode === 'SHOWROOM' ? 'GARAGAT' : 'SELF';
  const anyGaragat = pickupMethod === 'GARAGAT' || returnMethod === 'GARAGAT';
  const values = Object.values(s.selectedValues);
  return {
    buyerCustomerId: s.buyer!.customerId,
    vehicleId: s.vehicle!.vehicleId,
    garageId: s.garage!.id,
    serviceOfferingId: s.service!.id,
    variantIds: s.variantIds.length > 0 ? s.variantIds : undefined,
    selectedVariationValueIds: values.length > 0 ? values : undefined,
    scheduledDate: s.date,
    pickupMethod,
    returnMethod,
    pickupTimeSlotId: s.pickupSlotId ?? undefined,
    returnTimeSlotId: s.returnSlotId ?? undefined,
    pickupShowroomId: pickupMethod === 'GARAGAT' ? s.pickupShowroomId ?? undefined : undefined,
    dropoffShowroomId: returnMethod === 'GARAGAT' ? s.dropoffShowroomId ?? undefined : undefined,
    transportType: anyGaragat ? s.transportType : undefined,
    notes: s.notes.trim() || undefined,
    expectedTotalPayable,
    buyerEmail: s.buyer!.email,
    buyerPhone: s.buyer!.phone,
  };
}

interface BookingWizardProps {
  token: string;
  showrooms: Showroom[];
  onBooked: (booking: DealerBooking) => void;
  /** The wallet switch: on, a buyer without credit is sent to "Assign credit"; off, to a per-buyer request. */
  walletEnabled?: boolean;
}

export default function BookingWizard({ token, showrooms, onBooked, walletEnabled = false }: BookingWizardProps) {
  const { t, lang } = useLanguage();
  const [s, setS] = useState<WizardState>(loadWizard);
  const [quote, setQuote] = useState<DealerBookingQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      sessionStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(s));
    } catch {
      // Storage full or blocked — the wizard still works in memory.
    }
  }, [s]);

  const update = (patch: Partial<WizardState>) => setS((prev) => ({ ...prev, ...patch }));
  const stepKey: StepKey = STEPS[s.step];

  const runQuote = useCallback(async (state: WizardState) => {
    setBusy(true);
    setQuote(null);
    setQuoteError(null);
    try {
      setQuote(await dealerApi.quoteBooking(toBookingRequest(state), token));
    } catch (err) {
      setQuoteError(dealerErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  }, [token, t]);

  useEffect(() => {
    if (stepKey === 'quote') runQuote(s);
    // Re-quote only on entering the quote step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  const canNext = (): boolean => {
    switch (stepKey) {
      case 'buyer': return s.buyer != null;
      case 'car': return s.vehicle != null;
      case 'service': return s.garage != null && s.service != null && s.serviceReady;
      case 'slot': return !!s.date && s.pickupSlotId != null;
      case 'legs':
        return (s.pickupMode === 'SELF' || s.pickupShowroomId != null)
          && (s.returnMode === 'SELF' || s.dropoffShowroomId != null);
      case 'quote': return quote != null && Number(quote.toPayNowAed) === 0;
      default: return false;
    }
  };

  const go = (step: number) => { setError(null); update({ step }); };

  const confirm = async () => {
    if (!quote) return;
    setBusy(true);
    setError(null);
    try {
      const booking = await dealerApi.book(toBookingRequest(s, Number(quote.totalPayable)), token);
      try { sessionStorage.removeItem(WIZARD_STORAGE_KEY); } catch { /* ignore */ }
      onBooked(booking);
    } catch (err) {
      setError(dealerErrorMessage(err, t));
      if (errorCodeOf(err) === 'BOOKING_TOTAL_CHANGED' || errorCodeOf(err) === 'EXCEEDS_CREDIT') {
        // The price moved since the quote: show the new figures before another confirm.
        update({ step: STEPS.indexOf('quote') });
      }
    } finally {
      setBusy(false);
    }
  };

  const startOver = () => {
    try { sessionStorage.removeItem(WIZARD_STORAGE_KEY); } catch { /* ignore */ }
    setQuote(null);
    setError(null);
    setS(emptyWizard());
  };

  return (
    <div className="dealer-wizard">
      <ol className="dealer-steps" aria-label={t('dealer.wizard.progress')}>
        {STEPS.map((key, i) => (
          <li key={key} className={`dealer-steps__item${i === s.step ? ' is-current' : ''}${i < s.step ? ' is-done' : ''}`}
            aria-current={i === s.step ? 'step' : undefined}>
            <span className="dealer-steps__num">{i + 1}</span>
            <span className="dealer-steps__label">{t(`dealer.wizard.step.${key}`)}</span>
          </li>
        ))}
      </ol>

      <div className="dealer-card">
        <h3>{t(`dealer.wizard.step.${stepKey}`)}</h3>

        {stepKey === 'buyer' && (
          <BuyerStep
            token={token}
            walletEnabled={walletEnabled}
            buyer={s.buyer}
            onSelect={(buyer) => update({ buyer, vehicle: null })}
          />
        )}

        {stepKey === 'car' && s.buyer && (
          <CarStep token={token} customerId={s.buyer.customerId} selectedId={s.vehicle?.vehicleId ?? null}
            onSelect={(v) => update({ vehicle: v, serviceReady: false, selectedValues: {}, variantIds: [] })} />
        )}

        {stepKey === 'service' && s.vehicle && (
          <ServiceStep token={token} state={s} update={update} />
        )}

        {stepKey === 'slot' && (
          <SlotStep token={token} state={s} update={update} />
        )}

        {stepKey === 'legs' && (
          <div className="dealer-legs">
            <fieldset className="dealer-fieldset">
              <legend>{t('dealer.wizard.pickup')}</legend>
              <label className="dealer-radio">
                <input type="radio" name="pickupMode" checked={s.pickupMode === 'SHOWROOM'} disabled={showrooms.length === 0}
                  onChange={() => update({ pickupMode: 'SHOWROOM' })} />
                {t('dealer.wizard.pickupShowroom')}
              </label>
              {s.pickupMode === 'SHOWROOM' && (
                <ShowroomPicker name="pickupShowroom" showrooms={showrooms} selectedId={s.pickupShowroomId}
                  onSelect={(id) => update({ pickupShowroomId: id, dropoffShowroomId: s.dropoffShowroomId ?? id })} />
              )}
              <label className="dealer-radio">
                <input type="radio" name="pickupMode" checked={s.pickupMode === 'SELF'}
                  onChange={() => update({ pickupMode: 'SELF', transportType: 'RECOVERY' })} />
                {t('dealer.wizard.pickupSelf')}
              </label>
            </fieldset>
            <fieldset className="dealer-fieldset">
              <legend>{t('dealer.wizard.return')}</legend>
              <label className="dealer-radio">
                <input type="radio" name="returnMode" checked={s.returnMode === 'SHOWROOM'} disabled={showrooms.length === 0}
                  onChange={() => update({ returnMode: 'SHOWROOM' })} />
                {t('dealer.wizard.returnShowroom')}
              </label>
              {s.returnMode === 'SHOWROOM' && (
                <ShowroomPicker name="dropoffShowroom" showrooms={showrooms} selectedId={s.dropoffShowroomId}
                  onSelect={(id) => update({ dropoffShowroomId: id })} />
              )}
              <label className="dealer-radio">
                <input type="radio" name="returnMode" checked={s.returnMode === 'SELF'}
                  onChange={() => update({ returnMode: 'SELF', transportType: 'RECOVERY' })} />
                {t('dealer.wizard.returnSelf')}
              </label>
            </fieldset>
            {(s.pickupMode === 'SHOWROOM' || s.returnMode === 'SHOWROOM') && (
              <fieldset className="dealer-fieldset">
                <legend>{t('dealer.wizard.transport')}</legend>
                <label className="dealer-radio">
                  <input type="radio" name="transport" checked={s.transportType === 'RECOVERY'}
                    onChange={() => update({ transportType: 'RECOVERY' })} />
                  {t('dealer.wizard.transportRecovery')}
                </label>
                <label className="dealer-radio">
                  <input type="radio" name="transport" checked={s.transportType === 'ZOFEUR'}
                    disabled={!(s.pickupMode === 'SHOWROOM' && s.returnMode === 'SHOWROOM')}
                    onChange={() => update({ transportType: 'ZOFEUR' })} />
                  {t('dealer.wizard.transportZofeur')}
                </label>
                <p className="dealer-hint">{t('dealer.wizard.transportHint')}</p>
              </fieldset>
            )}
          </div>
        )}

        {stepKey === 'quote' && (
          <div data-testid="booking-quote">
            {busy && <p className="dealer-muted">{t('dealer.common.loading')}</p>}
            {quoteError && <div className="error-message" role="alert">{quoteError}</div>}
            {quote && (
              <>
                <dl className="dealer-dl">
                  <dt>{t('dealer.wizard.servicePrice')}</dt><dd><Money value={quote.servicePrice} /></dd>
                  {quote.deliveryFeeGross != null && Number(quote.deliveryFeeGross) > 0 && (
                    <><dt>{t('dealer.wizard.deliveryFee')}</dt><dd><Money value={quote.deliveryFeeGross} /></dd></>
                  )}
                  {quote.serviceFeeGross != null && Number(quote.serviceFeeGross) > 0 && (
                    <><dt>{t('dealer.wizard.serviceFee')}</dt><dd><Money value={quote.serviceFeeGross} /></dd></>
                  )}
                  <dt>{t('dealer.wizard.total')}</dt><dd><Money value={quote.totalPayable} /></dd>
                  <dt>{t('dealer.wizard.creditAvailable')}</dt><dd><Money value={quote.creditAvailableAed} /></dd>
                  <dt>{t('dealer.wizard.creditApplied')}</dt><dd><Money value={quote.creditAppliedAed} /></dd>
                  <dt className="dealer-dl__total">{t('dealer.wizard.toPayNow')}</dt>
                  <dd className="dealer-dl__total"><Money value={quote.toPayNowAed} /></dd>
                </dl>
                {Number(quote.toPayNowAed) > 0 && (
                  <div className="error-message" role="alert">
                    {fmt(t('dealer.booking.exceedsBy'), { amount: formatAed(quote.exceedsByAed ?? quote.toPayNowAed) })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {stepKey === 'confirm' && s.buyer && s.vehicle && s.garage && s.service && quote && (
          <div>
            <dl className="dealer-dl">
              <dt>{t('dealer.wizard.step.buyer')}</dt>
              <dd>{s.buyer.displayName || t('dealer.credits.unnamed')} · {t('dealer.lookup.garagatCustomer')}</dd>
              <dt>{t('dealer.wizard.step.car')}</dt>
              <dd>{[s.vehicle.make, s.vehicle.model].filter(Boolean).join(' ')} <span dir="ltr">{s.vehicle.plateNumber}</span></dd>
              <dt>{t('dealer.bookings.garage')}</dt><dd>{s.garage.name}</dd>
              <dt>{t('dealer.bookings.service')}</dt><dd>{s.service.name}</dd>
              <dt>{t('dealer.bookings.date')}</dt><dd>{formatDate(s.date, lang)}</dd>
              <dt>{t('dealer.wizard.pickup')}</dt>
              <dd>{s.pickupMode === 'SHOWROOM'
                ? showrooms.find((x) => x.id === s.pickupShowroomId)?.label
                : t('dealer.wizard.pickupSelf')}</dd>
              <dt>{t('dealer.wizard.return')}</dt>
              <dd>{s.returnMode === 'SHOWROOM'
                ? showrooms.find((x) => x.id === s.dropoffShowroomId)?.label
                : t('dealer.wizard.returnSelf')}</dd>
              <dt className="dealer-dl__total">{t('dealer.wizard.creditApplied')}</dt>
              <dd className="dealer-dl__total"><Money value={quote.creditAppliedAed} /></dd>
            </dl>
            <div className="form-group">
              <label htmlFor="booking-notes">{t('dealer.wizard.notes')}</label>
              <textarea id="booking-notes" className="dealer-textarea" maxLength={1000} value={s.notes}
                onChange={(e) => update({ notes: e.target.value })} />
            </div>
            <p className="dealer-muted">{t('dealer.wizard.confirmNote')}</p>
          </div>
        )}

        {error && <div className="error-message" role="alert">{error}</div>}

        <div className="dealer-actions dealer-wizard__nav">
          {s.step > 0 && (
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => go(s.step - 1)}>
              {t('dealer.common.back')}
            </button>
          )}
          {stepKey !== 'confirm' ? (
            <button type="button" className="btn btn-primary" disabled={!canNext() || busy} onClick={() => go(s.step + 1)}>
              {t('dealer.common.next')}
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={busy || !quote} onClick={confirm}>
              {busy ? t('dealer.common.saving') : t('dealer.wizard.confirm')}
            </button>
          )}
          <button type="button" className="btn btn-link" onClick={startOver}>{t('dealer.wizard.startOver')}</button>
        </div>
      </div>
    </div>
  );
}

// ── steps ──

/**
 * BR-8 / D-152 — find the customer by email + phone, then check they hold LIVE credit from this dealer (the
 * dealer's buyers list); a customer without it cannot be booked for (assign credit first).
 */
function BuyerStep({ token, walletEnabled, buyer, onSelect }: {
  token: string; walletEnabled: boolean; buyer: WizardState['buyer']; onSelect: (buyer: WizardState['buyer']) => void;
}) {
  const { t } = useLanguage();
  const [noCredit, setNoCredit] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async (c: FoundCustomer) => {
    setNoCredit(false);
    setError(null);
    try {
      const row = (await dealerApi.buyers(token)).find((b) => b.customerId === c.customerRef && b.hasAccount);
      if (!row) {
        setNoCredit(true);
        return;
      }
      onSelect({ customerId: c.customerRef, displayName: c.displayName, email: c.email, phone: c.phone, remainingAed: row.remainingAed });
    } catch (err) {
      setError(dealerErrorMessage(err, t));
    }
  };

  const confirmed: FoundCustomer | null = buyer
    ? { customerRef: buyer.customerId, displayName: buyer.displayName, email: buyer.email, phone: buyer.phone }
    : null;

  return (
    <div>
      <CustomerLookup token={token} idPrefix="wizard-buyer" confirmed={confirmed} onConfirm={confirm}
        onChange={() => { setNoCredit(false); onSelect(null); }} />
      {buyer && (
        <p className="dealer-muted">{t('dealer.wizard.creditAvailable')}: <Money value={buyer.remainingAed} /></p>
      )}
      {noCredit && (
        <div className="dealer-lookup-notice" role="status">
          <p>{t('dealer.wizard.noCreditForCustomer')}</p>
          {walletEnabled
            ? <Link className="btn btn-primary" to="/dealer/credit/assign">{t('dealer.credit.assignCta')}</Link>
            : <Link className="btn btn-primary" to="/dealer/credit/request">{t('dealer.credit.requestCta')}</Link>}
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
    </div>
  );
}

function CarStep({ token, customerId, selectedId, onSelect }: {
  token: string; customerId: string; selectedId: string | null; onSelect: (v: BuyerVehicle) => void;
}) {
  const { t } = useLanguage();
  const [vehicles, setVehicles] = useState<BuyerVehicle[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ make: '', model: '', year: '', plateNumber: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    dealerApi.buyerVehicles(customerId, token)
      .then((v) => { setVehicles(v); if (v.length === 0) setAdding(true); })
      .catch((err) => setError(dealerErrorMessage(err, t)));
  }, [customerId, token, t]);
  useEffect(() => { load(); }, [load]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const year = Number(form.year);
    if (!form.make.trim() || !form.model.trim() || !form.plateNumber.trim()
      || !/^\d{4}$/.test(form.year) || year < 1900 || year > 2100) {
      setFormError(t('dealer.wizard.carInvalid'));
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const created = await dealerApi.addBuyerVehicle(customerId, {
        label: `${form.make.trim()} ${form.model.trim()}`,
        make: form.make.trim(), model: form.model.trim(), year, plateNumber: form.plateNumber.trim(),
      }, token);
      const v: BuyerVehicle = { vehicleId: created.id, make: form.make.trim(), model: form.model.trim(), plateNumber: form.plateNumber.trim() };
      setVehicles((prev) => [...(prev ?? []), v]);
      setAdding(false);
      setForm({ make: '', model: '', year: '', plateNumber: '' });
      onSelect(v);
    } catch (err) {
      setFormError(dealerErrorMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="error-message">{error}</div>;
  if (!vehicles) return <p className="dealer-muted">{t('dealer.common.loading')}</p>;
  return (
    <>
      {vehicles.length > 0 && (
        <div className="dealer-choice-list" role="radiogroup">
          {vehicles.map((v) => (
            <label key={v.vehicleId} className={`dealer-choice${selectedId === v.vehicleId ? ' dealer-choice--selected' : ''}`}>
              <input type="radio" name="vehicle" checked={selectedId === v.vehicleId} onChange={() => onSelect(v)} />
              <span className="dealer-choice__main">
                <strong>{[v.make, v.model].filter(Boolean).join(' ') || t('dealer.wizard.car')}</strong>
                <span className="dealer-muted" dir="ltr">{v.plateNumber}</span>
              </span>
            </label>
          ))}
        </div>
      )}
      {vehicles.length === 0 && <p className="dealer-muted">{t('dealer.wizard.noCars')}</p>}
      {!adding ? (
        <button type="button" className="btn btn-secondary" onClick={() => setAdding(true)}>{t('dealer.wizard.addCar')}</button>
      ) : (
        <form className="dealer-inline-form" onSubmit={add} noValidate>
          <div className="dealer-grid-2">
            <div className="form-group">
              <label htmlFor="car-make">{t('dealer.wizard.carMake')}</label>
              <input id="car-make" value={form.make} maxLength={100} onChange={(e) => setForm({ ...form, make: e.target.value })} />
            </div>
            <div className="form-group">
              <label htmlFor="car-model">{t('dealer.wizard.carModel')}</label>
              <input id="car-model" value={form.model} maxLength={100} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            </div>
            <div className="form-group">
              <label htmlFor="car-year">{t('dealer.wizard.carYear')}</label>
              <input id="car-year" inputMode="numeric" dir="ltr" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
            </div>
            <div className="form-group">
              <label htmlFor="car-plate">{t('dealer.wizard.carPlate')}</label>
              <input id="car-plate" dir="ltr" value={form.plateNumber} maxLength={50} onChange={(e) => setForm({ ...form, plateNumber: e.target.value })} />
            </div>
          </div>
          {formError && <div className="error-message">{formError}</div>}
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? t('dealer.common.saving') : t('dealer.wizard.saveCar')}
          </button>
        </form>
      )}
    </>
  );
}

function ServiceStep({ token, state, update }: {
  token: string; state: WizardState; update: (p: Partial<WizardState>) => void;
}) {
  const { t } = useLanguage();
  const [keyword, setKeyword] = useState('');
  const [garages, setGarages] = useState<GarageSearchRow[] | null>(null);
  const [services, setServices] = useState<GarageServiceRow[] | null>(null);
  const [offering, setOffering] = useState<OfferingV2 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const vehicle = { make: state.vehicle?.make, model: state.vehicle?.model };

  const search = async (e?: FormEvent) => {
    e?.preventDefault();
    setError(null);
    try {
      const page = await dealerApi.searchGarages(keyword.trim(), token);
      setGarages(page.content ?? []);
    } catch (err) {
      setError(dealerErrorMessage(err, t));
    }
  };

  useEffect(() => {
    if (!state.garage) return;
    dealerApi.garageServices(state.garage.id, token).then(setServices).catch((err) => setError(dealerErrorMessage(err, t)));
  }, [state.garage, token, t]);

  useEffect(() => {
    if (!state.garage || !state.service) { setOffering(null); return; }
    dealerApi.offering(state.garage.id, state.service.id, vehicle, token)
      .then((o) => {
        setOffering(o);
        if (!o.variations || o.variations.length === 0) update({ serviceReady: true, variantIds: [], selectedValues: {} });
      })
      .catch((err) => setError(dealerErrorMessage(err, t)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.garage?.id, state.service?.id, token]);

  const resolve = async () => {
    if (!state.garage || !state.service || !offering?.variations) return;
    const values = offering.variations.map((v) => state.selectedValues[v.id]).filter(Boolean) as string[];
    if (values.length < offering.variations.length) {
      setError(t('dealer.wizard.pickOptions'));
      return;
    }
    setResolving(true);
    setError(null);
    try {
      const q = await dealerApi.resolveQuote(state.garage.id, state.service.id, vehicle, values, token);
      if (!q.isAvailable) {
        update({ serviceReady: false });
        setError(t('dealer.wizard.notForCar'));
      } else {
        const ids = q.resolvedVariantIds && q.resolvedVariantIds.length > 0
          ? q.resolvedVariantIds : (q.resolvedVariantId ? [q.resolvedVariantId] : []);
        update({ serviceReady: true, variantIds: ids });
      }
    } catch (err) {
      setError(err instanceof ApiError ? dealerErrorMessage(err, t) : t('dealer.error.generic'));
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="dealer-service-step">
      {!state.garage ? (
        <>
          <form className="dealer-toolbar" onSubmit={search}>
            <label htmlFor="garage-search" className="offer-visually-hidden">{t('dealer.wizard.searchGarage')}</label>
            <input id="garage-search" className="dealer-input" value={keyword} placeholder={t('dealer.wizard.searchGarage')}
              onChange={(e) => setKeyword(e.target.value)} />
            <button type="submit" className="btn btn-primary">{t('dealer.wizard.search')}</button>
          </form>
          {garages && garages.length === 0 && <p className="dealer-muted">{t('dealer.wizard.noGarages')}</p>}
          {garages && garages.length > 0 && (
            <div className="dealer-choice-list" role="radiogroup">
              {garages.map((g) => (
                <label key={g.id} className="dealer-choice">
                  <input type="radio" name="garage" onChange={() => update({ garage: { id: g.id, name: g.name }, service: null, serviceReady: false })} />
                  <span className="dealer-choice__main">
                    <strong>{g.name}</strong>
                    <span className="dealer-muted">{[g.area, g.city].filter(Boolean).join(', ')}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <p>
            <strong>{state.garage.name}</strong>{' '}
            <button type="button" className="btn btn-link" onClick={() => {
              update({ garage: null, service: null, serviceReady: false, selectedValues: {}, variantIds: [] });
              setServices(null);
            }}>{t('dealer.wizard.changeGarage')}</button>
          </p>
          {!services && <p className="dealer-muted">{t('dealer.common.loading')}</p>}
          {services && services.length === 0 && <p className="dealer-muted">{t('dealer.wizard.noServices')}</p>}
          {services && services.length > 0 && (
            <div className="dealer-choice-list" role="radiogroup">
              {services.map((sv) => (
                <label key={sv.id} className={`dealer-choice${state.service?.id === sv.id ? ' dealer-choice--selected' : ''}`}>
                  <input type="radio" name="service" checked={state.service?.id === sv.id}
                    onChange={() => update({ service: { id: sv.id, name: sv.name }, serviceReady: false, selectedValues: {}, variantIds: [] })} />
                  <span className="dealer-choice__main"><strong>{sv.name}</strong></span>
                  {sv.price != null && (
                    <span className="dealer-choice__side">
                      <span className="dealer-muted">{t('dealer.wizard.from')}</span> <Money value={sv.price} />
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
          {offering?.variations && offering.variations.length > 0 && (
            <div className="dealer-variations">
              {offering.variations.map((v) => (
                <fieldset key={v.id} className="dealer-fieldset">
                  <legend>{v.name}</legend>
                  {v.values.map((val) => (
                    <label key={val.id} className="dealer-radio">
                      <input type="radio" name={`variation-${v.id}`} checked={state.selectedValues[v.id] === val.id}
                        disabled={val.isAvailableForVehicle === false}
                        onChange={() => update({ selectedValues: { ...state.selectedValues, [v.id]: val.id }, serviceReady: false })} />
                      {val.label}
                      {val.quote?.price != null && <> · <Money value={val.quote.price} /></>}
                    </label>
                  ))}
                </fieldset>
              ))}
              <button type="button" className="btn btn-secondary" disabled={resolving} onClick={resolve}>
                {resolving ? t('dealer.common.loading') : t('dealer.wizard.checkPrice')}
              </button>
              {state.serviceReady && <p className="dealer-ok">{t('dealer.wizard.priceReady')}</p>}
            </div>
          )}
        </>
      )}
      {error && <div className="error-message">{error}</div>}
    </div>
  );
}

function SlotStep({ token, state, update }: {
  token: string; state: WizardState; update: (p: Partial<WizardState>) => void;
}) {
  const { t } = useLanguage();
  const [slots, setSlots] = useState<PickupTimeSlot[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dealerApi.pickupTimeSlots(token)
      .then((list) => setSlots([...list].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))))
      .catch((err) => setError(dealerErrorMessage(err, t)));
  }, [token, t]);

  return (
    <div>
      <div className="form-group">
        <label htmlFor="booking-date">{t('dealer.bookings.date')}</label>
        <input id="booking-date" type="date" className="dealer-input" min={todayIso()} value={state.date}
          onChange={(e) => update({ date: e.target.value })} />
      </div>
      {error && <div className="error-message">{error}</div>}
      {slots && (
        <div className="dealer-grid-2">
          <div className="form-group">
            <label htmlFor="pickup-slot">{t('dealer.wizard.pickupSlot')}</label>
            <select id="pickup-slot" className="dealer-select" value={state.pickupSlotId ?? ''}
              onChange={(e) => update({ pickupSlotId: e.target.value || null })}>
              <option value="">{t('dealer.wizard.chooseSlot')}</option>
              {slots.map((sl) => <option key={sl.id} value={sl.id}>{sl.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="return-slot">{t('dealer.wizard.returnSlot')}</label>
            <select id="return-slot" className="dealer-select" value={state.returnSlotId ?? ''}
              onChange={(e) => update({ returnSlotId: e.target.value || null })}>
              <option value="">{t('dealer.wizard.anySlot')}</option>
              {slots.map((sl) => <option key={sl.id} value={sl.id}>{sl.label}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
